import { LANGUAGE_DEFAULT, NError, NSuccess } from '@configs/const.config'
import { TResApi, TResApiErr, TResDataListApi } from '@configs/interface.config'
import { checkAuth } from '@src/libs/localStorage'
import { TEvent, TPatchEvent, TQueryEvent } from '@src/modules'
import { notification } from 'antd'
import { useInfiniteQuery, useMutation, useQuery } from 'react-query'

import { queryClient } from '..'
import {
  createEvent,
  getListEvent,
  getListEventApp,
  getEventById,
  getTotalEvent,
  patchEventById,
  putSlugEventById,
  removeManyEventByIds,
  removeEventById,
} from '../apis'
import { DETAIL_EVENT, LIST_EVENT, LIST_EVENT_INFINITE, TOTAL_EVENT } from '../keys'

/**
 *
 * @method useMutationCreateEvent
 * @returns
 */
export const useMutationCreateEvent = () =>
  useMutation(createEvent, {
    onSuccess: (res: TResApi<TEvent>) => {
      queryClient.refetchQueries([LIST_EVENT]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      // [TODO] ...
      notification.error({ message: NError, description: error?.message })
    },
  })

/**
 * @method useQueryListEvent
 * @param {TQueryEvent}params
 * @param {string}token
 * @param lang
 * @returns
 */
export const useQueryListEvent = (params: TQueryEvent, token?: string, lang = LANGUAGE_DEFAULT) => {
  const accessToken = token || checkAuth()
  return useQuery<TResDataListApi<TEvent[]>>(
    [LIST_EVENT, JSON.stringify(params), lang],
    () => getListEvent(params, accessToken, lang),
    { enabled: !!accessToken },
  )
}

/**
 * @method useQueryEventTotal
 * @param {string}token
 * @returns
 */
export const useQueryEventTotal = (token?: string) => {
  const accessToken = token || checkAuth()
  return useQuery<TResApi<number>>([TOTAL_EVENT], () => getTotalEvent(token), { enabled: !!accessToken })
}

/**
 * @method useQueryEventById
 * @param {string}id
 * @param {string}token
 * @param lang
 * @returns
 */
export const useQueryEventById = (id: string, token?: string, lang = LANGUAGE_DEFAULT) => {
  const accessToken = token || checkAuth()
  return useQuery<TResApi<TEvent>>([DETAIL_EVENT, id, lang], () => getEventById(id, token, lang), {
    enabled: !!accessToken && !!id,
  })
}

/**
 *
 * @method useMutationPatchEventById
 * @returns
 */
export const useMutationPatchEventById = () =>
  useMutation(({ id, data }: { id: string; data: TPatchEvent }) => patchEventById(id, data), {
    onSuccess: (res: TResApi, { id }) => {
      queryClient.refetchQueries([LIST_EVENT]).then()
      queryClient.invalidateQueries([DETAIL_EVENT, id]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      // [TODO] ...
      notification.error({ message: NError, description: error?.message })
    },
  })

/**
 *
 * @method useMutationRemoveEventById
 * @returns
 */
export const useMutationRemoveEventById = () =>
  useMutation((id: string) => removeEventById(id), {
    onSuccess: (res: TResApi, id) => {
      queryClient.refetchQueries([LIST_EVENT]).then()
      queryClient.removeQueries([DETAIL_EVENT, id])
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      // [TODO] ...
      notification.error({ message: NError, description: error?.message })
    },
  })

/**
 *
 * @method useMutationRemoveManyEventByIds
 * @returns
 */
export const useMutationRemoveManyEventByIds = () =>
  useMutation(removeManyEventByIds, {
    onSuccess: (res: TResApi) => {
      // [TODO] ...
      queryClient.invalidateQueries([LIST_EVENT]).then()
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      // [TODO] ...
      notification.error({ message: NError, description: error?.message })
    },
  })

/**
 *
 * @method useMutationPutSlugEventById
 * @returns
 */
export const useMutationPutSlugEventById = () =>
  useMutation(({ id, slug }: { id: string; slug: string }) => putSlugEventById(id, slug), {
    onSuccess: (res: TResApi, { id, slug }) => {
      queryClient.setQueryData([DETAIL_EVENT, id], () => {
        const old = queryClient.getQueryData<TResApi<TEvent>>([DETAIL_EVENT, id])
        if (old && old?.data)
          return {
            ...old,
            data: { ...old.data, slug },
          }
        return old
      })
      // queryClient.refetchQueries([LIST_POST])
      notification.success({ message: NSuccess, description: res?.message })
    },
    onError: (error: TResApiErr) => {
      // [TODO] ...
      notification.error({ message: NError, description: error?.message })
    },
  })

/**
 * useInfinteQueryListEvent
 * @param params
 * @param token
 * @returns
 */
export const useInfinteQueryListEvent = (params: TQueryEvent, token?: string) => {
  const accessToken = token || checkAuth()

  return useInfiniteQuery<TResDataListApi<TEvent[]>>(
    [LIST_EVENT_INFINITE, JSON.stringify(params)],
    ({ pageParam = 1 }) => getListEventApp({ ...params, page: pageParam }, accessToken),
    {
      refetchOnWindowFocus: true,
      retry: 2,
      enabled: !!accessToken,
      getNextPageParam: (lastPage) =>
        lastPage?.page < Math.ceil(lastPage.total / lastPage.limit) ? lastPage.page + 1 : undefined,
    },
  )
}
